import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Alert, AlertDescription, RefreshButton } from '../../components/ui';
import { AlertCircle, FlaskConical, Loader2, Package, Pin, PinOff, Search, Shield } from 'lucide-react';
import { MaturityBadge, maturityToTier as matToTier } from '../../components/ui/MaturityBadge';
import type { Tier } from '../../components/ui/MaturityBadge';
import { moduleDisplayName } from '../../lib/module-identity';
import type { WidgetRenderProps } from '../../widgets/types';

/** Per-user piny do levého menu (ui_prefs 'nav_pins'). Sdílený query key se
 *  sidebarem (AppLayout) — toggle invaliduje obojí, menu se překreslí. */
function useNavPins() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['ui-nav-pins'],
    queryFn: () => api.mcpSession<{ value?: { pins?: string[] } | null }>('ui.prefs_get', { key: 'nav_pins' }),
    staleTime: 5 * 60_000,
  });
  const pins = q.data?.value?.pins ?? [];
  const toggle = useMutation({
    mutationFn: (name: string) => {
      const next = pins.includes(name) ? pins.filter((p) => p !== name) : [...pins, name];
      return api.mcpSession<{ ok: boolean; skipped?: string }>('ui.prefs_set', {
        key: 'nav_pins',
        value: { pins: next },
      });
    },
    // Optimisticky přepni hned — server round-trip nesmí být jediná cesta k
    // vizuální změně (stejný vzor jako LanguageSwitcher.persistLang, který
    // funguje spolehlivě protože i18n+localStorage se mění lokálně nezávisle
    // na síti). Bez tohohle toggle vypadá jako "nic se neděje", i když
    // mutace doběhla — Filip 2026-07-22.
    onMutate: (name: string) => {
      const next = pins.includes(name) ? pins.filter((p) => p !== name) : [...pins, name];
      qc.setQueryData(['ui-nav-pins'], { value: { pins: next } });
    },
    onSuccess: (res) => {
      // ui.prefs_set vrací ok:true i když zápis tiše přeskočil (chybějící
      // entity_id) — bez tyhle kontroly se to nikdy neprojeví jako chyba.
      if (res?.skipped) {
        toast.error(t('modules.pinNotSavedRefresh'));
      }
      qc.invalidateQueries({ queryKey: ['ui-nav-pins'] });
      qc.invalidateQueries({ queryKey: ['modules-nav'] });
    },
    onError: (e: unknown) => {
      toast.error(String((e as Error).message));
      qc.invalidateQueries({ queryKey: ['ui-nav-pins'] });
    },
  });
  return { pins, toggle };
}

interface AcmModule {
  name: string;
  version: string;
  description: string;
  maturity?: string;
  core?: boolean;
  /** Strojová identita + anglický familiar name podle účelu. */
  uuid?: string | null;
  canonical_name?: string | null;
  /** `component` = vnitřní stavební díl platformy (volá ho lifecycle /
   *  orchestrátor), NE modul pro obsluhu. */
  type?: string | null;
  /** Vlastník komponenty — modul, který ji volá/spravuje. */
  parent?: string | null;
  section: string | null;
  tools: string[];
  ui?: { label: string; icon: string; section: string } | null;
}

// Route jen z module.json `core` (kontrakt UI identity 2026-07-24) — žádný
// hardcoded seznam jmen, žádné dev/test štěpení. Seznam modulů je DYNAMICKÝ
// z /api/modules (listModules z disku); tahle mřížka jen zobrazuje non-core.
function moduleRoute(name: string, isCore: boolean): string {
  return isCore ? `/core/${name}` : `/modules/${name}`;
}

const COLORS = [
  { ring: 'border-blue-500/20', bg: 'bg-blue-600/10', icon: 'text-blue-400' },
  { ring: 'border-amber-500/20', bg: 'bg-amber-600/10', icon: 'text-amber-400' },
  { ring: 'border-rose-500/20', bg: 'bg-rose-600/10', icon: 'text-rose-400' },
  { ring: 'border-cyan-500/20', bg: 'bg-cyan-600/10', icon: 'text-cyan-400' },
  { ring: 'border-emerald-500/20', bg: 'bg-emerald-600/10', icon: 'text-emerald-400' },
  { ring: 'border-violet-500/20', bg: 'bg-violet-600/10', icon: 'text-violet-400' },
];

function ModuleCard(
  { mod, i, tier, pinned, onTogglePin }: {
    mod: AcmModule;
    i: number;
    tier: Tier;
    pinned: boolean;
    onTogglePin: (name: string) => void;
  },
) {
  const { t } = useTranslation();
  const c = COLORS[i % COLORS.length]!;

  return (
    <Link
      key={mod.name}
      to={moduleRoute(mod.name, mod.core ?? false)}
      className='group relative rounded-xl border border-gray-800 hover:border-gray-700 bg-gray-900/20 hover:bg-gray-900/50 transition-all p-3 flex items-center gap-3'
    >
      <div
        className={`w-8 h-8 rounded-lg ${c.bg} border ${c.ring} flex items-center justify-center flex-shrink-0`}
      >
        <Package size={15} className={c.icon} />
      </div>
      <div className='flex-1 min-w-0'>
        <div className='flex items-baseline gap-1.5'>
          <span className='text-sm font-semibold text-white'>{moduleDisplayName(mod, (k, fb) => t(k, fb ?? ''))}</span>
          <span className='text-[10px] text-gray-600 font-mono'>v{mod.version}</span>
          <MaturityBadge tier={tier} />
          <span className='ml-auto text-[10px] text-gray-600 font-mono flex-shrink-0'>
            {t('modules.toolsCount', { count: mod.tools.length })}
          </span>
        </div>
        <p className='text-xs text-gray-500 truncate mt-0.5'>{mod.description}</p>
      </div>
      {/* Pin jen u modulů, které se v levém menu vůbec můžou zobrazit (mají ui).
          Moduly bez ui (ui/notify/gitlab/editions) jdou dřív připnout naprázdno —
          pref se uložil, ale sidebar je nezobrazí → matoucí. */}
      {mod.ui && (
        <button
          type='button'
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onTogglePin(mod.name);
          }}
          title={pinned
            ? t('modules.pinRemove', 'Odebrat z levého menu')
            : t('modules.pinAdd', 'Přidat do levého menu')}
          className={`flex-shrink-0 p-1.5 rounded-lg transition-colors ${
            pinned
              ? 'text-blue-400 hover:text-blue-300 hover:bg-blue-500/10'
              : 'text-gray-600 hover:text-gray-300 hover:bg-gray-800 opacity-0 group-hover:opacity-100'
          }`}
        >
          {pinned ? <Pin size={14} /> : <PinOff size={14} />}
        </button>
      )}
    </Link>
  );
}

export function ModulesBrowsePanel(_props: WidgetRenderProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const { data, isLoading, isError, error, refetch } = useQuery<{ modules: AcmModule[] }>({
    queryKey: ['modules-list'],
    queryFn: () => api.mcp<{ modules: AcmModule[] }>('acm-self.modules_list'),
    staleTime: 30_000,
  });

  const modules = data?.modules ?? [];
  const { pins, toggle } = useNavPins();
  const q = search.trim().toLowerCase();

  // Rozdelit podle tier
  const grouped = {
    core: [] as AcmModule[],
    stable: [] as AcmModule[],
    test: [] as AcmModule[],
    dev: [] as AcmModule[],
  };
  // Mřížka je pro moduly OBSLUHY. Vnitřní stavební díly platformy
  // (type=component, např. supabase-template / acm-db / mcp-proxy) a moduly
  // bez UI do ní nepatří — klik by vedl na prázdnou stránku a v seznamu
  // vypadaly jako plnohodnotné moduly (Filip 2026-08-25). Počet se přiznává
  // v patičce, ať nic nezmizí potichu.
  const hiddenComponents: AcmModule[] = [];
  for (const m of modules) {
    if (m.core) continue; // core moduly (povinné) jsou v hlavním menu, ne v mřížce
    if (!m.ui || m.type === 'component') {
      hiddenComponents.push(m);
      continue;
    }
    const tier = matToTier(m.maturity);
    const label = moduleDisplayName(m, (k, fb) => t(k, fb ?? '')).toLowerCase();
    if (
      q &&
      !m.name.toLowerCase().includes(q) &&
      !label.includes(q) &&
      !(m.description ?? '').toLowerCase().includes(q)
    ) {
      continue;
    }
    grouped[tier].push(m);
  }

  const sections: { key: Tier; label: string; icon: typeof Shield; modules: AcmModule[] }[] = [
    // core moduly jsou v hlavnim menu
    { key: 'stable', label: t('modules.sectionStable'), icon: Package, modules: grouped.stable },
    { key: 'test', label: t('modules.sectionTesting'), icon: FlaskConical, modules: grouped.test },
    { key: 'dev', label: t('modules.sectionDevelopment'), icon: FlaskConical, modules: grouped.dev },
  ];
  const visibleCount = grouped.stable.length + grouped.test.length + grouped.dev.length;

  return (
    <div className='space-y-4'>
      <div className='flex items-center gap-2'>
        <div className='flex-1 flex items-center gap-2 bg-gray-900 border border-gray-800 rounded-xl px-3 py-2'>
          <Search size={14} className='text-gray-600' />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('modules.searchPlaceholder', 'Hledat modul…')}
            className='flex-1 bg-transparent text-sm text-white placeholder-gray-600 outline-none'
          />
        </div>
        <RefreshButton onRefresh={() => refetch()} loading={isLoading} size='md' />
      </div>

      {isLoading && (
        <div className='flex items-center gap-2 text-gray-500 text-sm'>
          <Loader2 size={14} className='animate-spin' />
          {t('modules.loadingModules')}
        </div>
      )}

      {isError && (
        <Alert variant='destructive'>
          <AlertDescription className='flex items-center gap-2'>
            <AlertCircle size={14} className='flex-shrink-0' />
            {error instanceof Error ? error.message : t('modules.loadFailed')}
          </AlertDescription>
        </Alert>
      )}

      {sections.map(({ key, label, icon: Icon, modules: sectionModules }) => {
        if (sectionModules.length === 0) return null;

        return (
          <div key={key} className='space-y-2'>
            <div className='flex items-center gap-2'>
              <Icon size={12} className='text-gray-400' />
              <span className='text-xs font-semibold uppercase tracking-widest text-gray-500'>
                {label}
              </span>
              <span className='text-xs text-gray-600'>{sectionModules.length}</span>
            </div>
            <div className='grid grid-cols-2 gap-2'>
              {sectionModules.map((mod, i) => (
                <ModuleCard
                  key={mod.name}
                  mod={mod}
                  i={i}
                  tier={key}
                  pinned={pins.includes(mod.name)}
                  onTogglePin={(n) => toggle.mutate(n)}
                />
              ))}
            </div>
          </div>
        );
      })}

      {!isLoading && !isError && q && visibleCount === 0 && (
        <p className='text-sm text-gray-500 py-6 text-center'>
          {t('modules.nothingFound', 'Nic nenalezeno.')}
        </p>
      )}

      {hiddenComponents.length > 0 && !q && (
        <p className='text-[11px] text-gray-600 pt-2 border-t border-gray-800/60'>
          {t('modules.platformComponentsNote', {
            count: hiddenComponents.length,
            owners: [...new Set(hiddenComponents.map((c) => c.parent).filter(Boolean))]
              .sort()
              .join(', '),
            defaultValue:
              '+ {{count}} vnitřních stavebních dílů platformy (bez vlastní stránky) — spravují je: {{owners}}',
          })}
        </p>
      )}
    </div>
  );
}
